import React from 'react';
import { XCircle, Check, ArrowRight } from 'lucide-react';

const Story: React.FC = () => {
  return (
    <section className="py-16 md:py-24 bg-gray-50 border-y border-gray-100 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12 md:mb-16 relative z-10">
          <div className="inline-flex items-center px-6 py-2.5 rounded-full bg-black mb-6 md:mb-8">
              <span className="text-white text-xs md:text-sm font-bold uppercase tracking-widest">
                  The Founder's Dilemma
              </span>
          </div>
          <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6 tracking-tight">
            You've Been There. <br className="hidden md:block" />We Know Exactly Where You're Standing.
          </h2>
          <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Great supplement ideas die in the execution gap. Great supplement brands get stuck with unreliable manufacturers. We bridge that gap.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 md:gap-12 relative z-10">
          {/* Rohan's Story - The New Founder */}
          <div className="bg-white rounded-3xl p-6 md:p-10 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 relative overflow-hidden group">
            {/* Slick Left Frame - Appears on Hover */}
            <div className="absolute top-0 bottom-0 left-0 w-1.5 bg-orange-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
             
            <div className="flex justify-between items-start mb-6 md:mb-8">
                <div>
                    <h3 className="text-2xl md:text-3xl font-extrabold text-gray-900">The Visionary</h3>
                    <div className="flex items-center mt-2 space-x-2">
                        <span className="h-1.5 w-1.5 rounded-full bg-orange-500"></span>
                        <p className="text-xs md:text-sm font-bold text-gray-500 uppercase tracking-wide">Rohan • Fitness Coach</p>
                    </div>
                </div>
                <span className="bg-orange-50 text-orange-800 text-[11px] md:text-xs font-black px-3 py-1.5 rounded uppercase tracking-wider">
                    0 to 1
                </span>
            </div>

            <div className="space-y-6">
                <div>
                    <p className="text-base md:text-lg text-gray-700 font-medium leading-relaxed">
                        Selling other brands' protein meant building someone else's dream for a 10% commission. He wanted ownership.
                    </p>
                </div>

                <div className="bg-red-50/50 rounded-2xl p-4 md:p-6 border border-red-100">
                    <h4 className="text-xs font-bold text-red-700 uppercase tracking-widest mb-4 flex items-center">
                        <XCircle className="w-4 h-4 mr-2" /> The Struggle
                    </h4>
                    <ul className="space-y-3">
                        <li className="flex items-start text-gray-700 text-sm md:text-base">
                            <span className="text-red-300 mr-3 text-lg">•</span>
                            <span>Manufacturers demanded <strong className="text-gray-900">5,000 unit MOQs</strong> he couldn't afford.</span>
                        </li>
                        <li className="flex items-start text-gray-700 text-sm md:text-base">
                            <span className="text-red-300 mr-3 text-lg">•</span>
                            <span>FSSAI compliance felt like deciphering a foreign language.</span>
                        </li>
                        <li className="flex items-start text-gray-700 text-sm md:text-base">
                            <span className="text-red-300 mr-3 text-lg">•</span>
                            <span>Zero guidance on formulation or flavoring.</span>
                        </li>
                    </ul>
                </div>

                <div className="flex items-center justify-center py-2">
                    <div className="h-px bg-gray-200 w-full"></div>
                    <span className="px-4 text-gray-600 text-xs md:text-sm font-medium whitespace-nowrap">The Turning Point</span>
                    <div className="h-px bg-gray-200 w-full"></div>
                </div>

                {/* Rohan's Result Box */}
                <div className="bg-gradient-to-br from-gray-500/[0.08] to-gray-500/[0.02] rounded-2xl p-4 md:p-6 border border-gray-100 relative overflow-hidden">
                    <div className="relative z-10">
                        <div className="flex items-center mb-5">
                            <div className="bg-white p-1.5 rounded-lg mr-3 border border-gray-100 shadow-sm">
                                <Check className="w-4 h-4 text-orange-600" strokeWidth={3} />
                            </div>
                            <span className="font-bold text-base md:text-lg tracking-tight text-gray-900">Partnered with BuildMySupplement</span>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4 md:gap-6 pt-5 border-t border-gray-200">
                            <div>
                                <div className="text-gray-500 text-[10px] md:text-xs font-bold uppercase mb-1 tracking-wider">Time to Market</div>
                                <div className="text-xl md:text-2xl font-bold text-gray-900">60 Days</div>
                            </div>
                            <div>
                                <div className="text-gray-500 text-[10px] md:text-xs font-bold uppercase mb-1 tracking-wider">First Batch</div>
                                <div className="text-xl md:text-2xl font-bold text-orange-600">Sold Out</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
          </div>

          {/* Priya's Story - The Scaling Brand */}
          <div className="bg-white rounded-3xl p-6 md:p-10 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 relative overflow-hidden group">
            {/* Slick Left Frame - Appears on Hover */}
            <div className="absolute top-0 bottom-0 left-0 w-1.5 bg-black opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
             
            <div className="flex justify-between items-start mb-6 md:mb-8">
                <div>
                    <h3 className="text-2xl md:text-3xl font-extrabold text-gray-900">The Scaler</h3>
                    <div className="flex items-center mt-2 space-x-2">
                        <span className="h-1.5 w-1.5 rounded-full bg-black"></span>
                        <p className="text-xs md:text-sm font-bold text-gray-500 uppercase tracking-wide">Priya • Ayurvedic D2C</p>
                    </div>
                </div>
                <span className="bg-gray-100 text-gray-600 text-[10px] md:text-xs font-black px-3 py-1.5 rounded uppercase tracking-wider">
                    1 to 10
                </span>
            </div>

            <div className="space-y-6">
                <div>
                    <p className="text-base md:text-lg text-gray-700 font-medium leading-relaxed">
                        She had product-market fit and loyal customers. But her supply chain was a ticking time bomb holding her back.
                    </p>
                </div>

                <div className="bg-gray-50 rounded-2xl p-4 md:p-6 border border-gray-100">
                    <h4 className="text-xs font-bold text-gray-700 uppercase tracking-widest mb-4 flex items-center">
                        <XCircle className="w-4 h-4 mr-2" /> The Bottleneck
                    </h4>
                    <ul className="space-y-3">
                        <li className="flex items-start text-gray-700 text-sm md:text-base">
                            <span className="text-gray-300 mr-3 text-lg">•</span>
                            <span>Inconsistent quality: Batch 3 clumped, Batch 4 tasted off.</span>
                        </li>
                        <li className="flex items-start text-gray-700 text-sm md:text-base">
                            <span className="text-gray-300 mr-3 text-lg">•</span>
                            <span>Ghosted by manufacturers when she needed answers.</span>
                        </li>
                        <li className="flex items-start text-gray-700 text-sm md:text-base">
                            <span className="text-gray-300 mr-3 text-lg">•</span>
                            <span>"2 weeks" lead time constantly turned into 6 weeks.</span>
                        </li>
                    </ul>
                </div>

                <div className="flex items-center justify-center py-2">
                    <div className="h-px bg-gray-200 w-full"></div>
                    <span className="px-4 text-gray-600 text-xs md:text-sm font-medium whitespace-nowrap">The Upgrade</span>
                    <div className="h-px bg-gray-200 w-full"></div>
                </div>

                {/* Priya's Result Box */}
                <div className="bg-gradient-to-br from-orange-500/[0.06] to-orange-500/[0.02] rounded-2xl p-4 md:p-6 border border-orange-100 relative overflow-hidden">
                    <div className="relative z-10">
                        <div className="flex items-center mb-5">
                            <div className="bg-white p-1.5 rounded-lg mr-3 border border-orange-100 shadow-sm">
                                <Check className="w-4 h-4 text-orange-600" strokeWidth={3} />
                            </div>
                            <span className="font-bold text-base md:text-lg tracking-tight text-gray-900">Switched to BuildMySupplement</span>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4 md:gap-6 pt-5 border-t border-orange-200">
                            <div>
                                <div className="text-gray-500 text-[10px] md:text-xs font-bold uppercase mb-1 tracking-wider">Reliability</div>
                                <div className="text-xl md:text-2xl font-bold text-gray-900">100%</div>
                            </div>
                            <div>
                                <div className="text-gray-500 text-[10px] md:text-xs font-bold uppercase mb-1 tracking-wider">Growth (QoQ)</div>
                                <div className="text-xl md:text-2xl font-bold text-gray-900 flex items-center">
                                    40% <ArrowRight className="w-5 h-5 ml-1 text-orange-500" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
          </div>
        </div>

        <div className="mt-16 md:mt-20 text-center">
             <p className="text-xl md:text-2xl font-medium text-gray-900 px-4">
                 Whether you're starting or scaling, we solve one problem: <span className="text-orange-600 font-bold underline decoration-4 underline-offset-4 decoration-orange-200">Execution</span>.
             </p>
        </div>
      </div>
    </section>
  );
};

export default Story;